import React, { useMemo } from 'react';

import {
	type ButtonComponent as LoomPluginButtonComponent,
	type LoomPluginOptions,
} from '@atlaskit/editor-plugins/loom';

import { useLoomEntryPointVariant } from '@confluence/loom-utils';

import { type UseLoomPluginOptionsProps } from '../loomEditorUtils-types';

type UseLoomPlugin = {
	isPluginEnabled: boolean;
	pluginOptions: LoomPluginOptions;
};

export const useLoomPluginOptions = ({
	EditorButtonComponent,
	editorAPIRef,
}: UseLoomPluginOptionsProps): UseLoomPlugin => {
	const { entryPointVariant } = useLoomEntryPointVariant();
	/**
	 * The Loom plugin should be enabled for all non-disabled Loom entrypoints.
	 *
	 * This has to be determined on the initial call of the hook (due to the way plugin initialization works).
	 * It should also still be OK if the plugin is enabled even for edge cases where the entrypoint is
	 * asynchronously determined to be disabled (e.g., when a site has opted out of cross-flow ads) because:
	 *
	 * `useInitializeLoomPlugin` will handle the conditions for initializing the plugin (to enable recording) and
	 * `EditorButtonComponent` will handle conditional rendering (in some cases, even returning `null` to dynamically
	 * hide the button)
	 */
	const isPluginEnabled = entryPointVariant !== 'DISABLED';

	const pluginOptions: LoomPluginOptions = useMemo(() => {
		return {
			renderButton: (ButtonComponent: LoomPluginButtonComponent) => {
				return (
					<EditorButtonComponent buttonComponent={ButtonComponent} editorAPIRef={editorAPIRef} />
				);
			},
		};
		// Intentionally not recreating/updating the plugin options in order to remain consistent with
		// the behavior of the editor preset that uses this hook to generate the loom plugin options
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { isPluginEnabled, pluginOptions };
};
